/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';
import { Fragment, useContext, useEffect, useState } from 'react';

import IntegrityFlagsPanelSlideOut from 'bundles/academic-integrity/components/IntegrityFlagsPanelSlideOut';
import type { SaveData } from 'bundles/author-assignment-grading/components/common/components/GraderModuleSidebar/PlagiarismContext';
import { PlagiarismContext } from 'bundles/author-assignment-grading/components/common/components/GraderModuleSidebar/PlagiarismContext';
import type { AssignmentContent } from 'bundles/author-common/types/Item';
import { SGAPageContext } from 'bundles/grading/components/staff-graded-assignments/SGAPageContext';

type Props = {
  isOpen: boolean;
  onClose(): void;
  submitterId?: string;
  submitterName?: string;
};

const PlagiarismDrawer: React.FC<Props> = ({ isOpen, onClose, submitterId, submitterName }) => {
  const {
    team,
    cheatingCaseCommentsByCase,
    cheatingCaseStaffComments,
    submissionKey,
    groupId,
    isAnonymized,
    learners,
    saving,
    ruling,
    canStaffComment,
    guiltyMembers,
    canUpdatePlagiarismFlag,
    saveData,
  } = useContext(PlagiarismContext);
  const {
    pageData: { currentItem, courseId, currentItemId },
  } = useContext(SGAPageContext);

  const [draftData, setDraftData] = useState<SaveData>({
    comment: '',
    ruling,
    guiltyMembers: [],
  });

  const isReadOnly = !submissionKey || !canUpdatePlagiarismFlag || !canStaffComment;

  const assignmentId = (currentItem as AssignmentContent).definition.assignmentId;

  useEffect(() => {
    setDraftData({
      comment: '',
      ruling,
      guiltyMembers,
    });
  }, [ruling, guiltyMembers, isOpen]);

  const handleSave = async () => {
    await saveData(draftData);
    setDraftData({ ...draftData, comment: '' });
  };

  return (
    <Fragment>
      <IntegrityFlagsPanelSlideOut
        isOpen={isOpen}
        team={team}
        groupId={groupId}
        isAnonymized={isAnonymized}
        isReadOnly={isReadOnly}
        guiltyMembers={draftData.guiltyMembers}
        pendingComment={draftData.comment}
        ruling={draftData.ruling}
        saving={saving}
        cheatingCaseStaffComments={cheatingCaseStaffComments}
        cheatingCaseCommentsByCase={cheatingCaseCommentsByCase}
        onSlideOutSave={handleSave}
        onSlideOutClose={onClose}
        onSelectStatus={(selection) => setDraftData((current) => ({ ...current, ruling: selection }))}
        onUpdateComment={(comment) => setDraftData((current) => ({ ...current, comment }))}
        onUpdateGuiltyMembers={(guilty) => setDraftData((current) => ({ ...current, guiltyMembers: guilty }))}
        learners={learners}
        assignmentId={assignmentId}
        submitterId={submitterId ?? ''}
        submitterName={submitterName}
        courseId={courseId}
        itemId={currentItemId}
      />
    </Fragment>
  );
};

export default PlagiarismDrawer;
